/**
 * PRD.330 Phase 2 — React binding for the report UI state reducer.
 *
 * The report consumer owns the controller: it reads `controller.query` (the
 * applied Phase 1 ReportQuery) to execute the report, and hands the
 * controller to <ReportShell> for interaction.
 */
import { useCallback, useMemo, useReducer } from 'react';
import {
  type ReportColumnFilter,
  type ReportDatePresetId,
  type ReportDateRange,
  type ReportDefinition,
  type ReportQuery,
  type ReportSortDirection,
} from '@helios/domain';
import {
  defaultReportUiState,
  initialReportUiState,
  reportUiReducer,
  type ReportFilterDraft,
  type ReportUiAction,
  type ReportUiState,
} from './report-state-logic';

export interface ReportController<C extends string> {
  definition: ReportDefinition<C>;
  state: ReportUiState<C>;
  query: ReportQuery<C>;
  openFilter: (columnId: C, facetValues: ReadonlyArray<string>) => void;
  editDraft: (draft: ReportFilterDraft) => void;
  selectVisible: (visibleValues: ReadonlyArray<string>) => void;
  clearVisible: (visibleValues: ReadonlyArray<string>) => void;
  applyDraft: (facetValues: ReadonlyArray<string>) => void;
  cancelDraft: () => void;
  clearColumn: (columnId: C) => void;
  /** Immediately set (or clear, with `undefined`) one column's applied filter. */
  setColumnFilter: (columnId: C, filter: ReportColumnFilter | undefined) => void;
  clearFilters: () => void;
  toggleSort: (columnId: C) => void;
  setSort: (columnId: C, direction: ReportSortDirection) => void;
  setSearch: (search: string) => void;
  setDateRange: (from: string, to: string) => void;
  selectDatePreset: (id: ReportDatePresetId) => void;
  selectCustomDate: () => void;
  /** Drill into a time-series period (sets the date range; remembers the previous one). */
  drillDateRange: (range: ReportDateRange) => void;
  /** Return to the date range before the last drill-down. */
  drillBack: () => void;
  setPage: (page: number) => void;
  clampPage: (totalCount: number) => void;
  reset: () => void;
}

export function useReportController<C extends string>(
  definition: ReportDefinition<C>,
  initialQuery?: ReportQuery<C>,
): ReportController<C> {
  const reducer = useCallback(
    (state: ReportUiState<C>, action: ReportUiAction<C>) =>
      reportUiReducer(definition, state, action),
    [definition],
  );
  // Default: the definition's default preset. An explicit initial query is Custom.
  const [state, dispatch] = useReducer(reducer, undefined, () =>
    initialQuery ? initialReportUiState(initialQuery) : defaultReportUiState(definition),
  );

  return useMemo(
    () => ({
      definition,
      state,
      query: state.query,
      openFilter: (columnId, facetValues) =>
        dispatch({ type: 'openFilter', columnId, facetValues }),
      editDraft: (draft) => dispatch({ type: 'editDraft', draft }),
      selectVisible: (visibleValues) => dispatch({ type: 'selectVisible', visibleValues }),
      clearVisible: (visibleValues) => dispatch({ type: 'clearVisible', visibleValues }),
      applyDraft: (facetValues) => dispatch({ type: 'applyDraft', facetValues }),
      cancelDraft: () => dispatch({ type: 'cancelDraft' }),
      clearColumn: (columnId) => dispatch({ type: 'clearColumn', columnId }),
      setColumnFilter: (columnId, filter) =>
        dispatch({ type: 'setColumnFilter', columnId, filter }),
      clearFilters: () => dispatch({ type: 'clearFilters' }),
      toggleSort: (columnId) => dispatch({ type: 'toggleSort', columnId }),
      setSort: (columnId, direction) => dispatch({ type: 'setSort', columnId, direction }),
      setSearch: (search) => dispatch({ type: 'setSearch', search }),
      setDateRange: (from, to) => dispatch({ type: 'setDateRange', from, to }),
      selectDatePreset: (id) => dispatch({ type: 'selectDatePreset', id }),
      selectCustomDate: () => dispatch({ type: 'selectCustomDate' }),
      drillDateRange: (range) => dispatch({ type: 'drillDateRange', range }),
      drillBack: () => dispatch({ type: 'drillBack' }),
      setPage: (page) => dispatch({ type: 'setPage', page }),
      clampPage: (totalCount) => dispatch({ type: 'clampPage', totalCount }),
      reset: () => dispatch({ type: 'reset' }),
    }),
    [definition, state],
  );
}
